import LegalPage, { H2, P, UL } from "@/components/LegalPage";
import { BRAND } from "@/config/brand";

export default function Terms() {
  return (
    <LegalPage eyebrow="Legal" title="Terms & Conditions"
      updated="8 February 2026" testid="terms-page">
      <P>
        These Terms and Conditions (&ldquo;Terms&rdquo;) govern your access to and use of
        the Counseltaria™ platform available at {BRAND.website} and the counselling
        services offered through it. By registering an account, booking a session or
        otherwise using the platform, you agree to these Terms in full. Please read
        them carefully — and if you are under 18, please read them together with a
        parent or legal guardian.
      </P>

      <H2>1. Who we are</H2>
      <P>
        Counseltaria™ is a private tele-counselling platform based in India that
        connects clients — of every age — with qualified mental-health professionals
        for one-to-one video sessions. When a client is under 18, an adult books
        and pays on their behalf.
      </P>

      <H2>2. Eligibility &amp; parental consent</H2>
      <UL>
        <li>You must be at least 18 years old to register an account and be the Booker/Payer on Counseltaria™.</li>
        <li>Clients under 18 can only receive counselling if an adult (parent or legal guardian) books, pays and manages the session on their behalf.</li>
        <li>By continuing to book on behalf of a minor, the parent/guardian confirms they have the legal right to do so.</li>
      </UL>

      <H2>3. Nature of service — not an emergency service</H2>
      <P>
        Counseltaria™ offers scheduled, private counselling sessions. It is <strong>not
        a crisis or emergency service</strong>. If you are experiencing thoughts of
        self-harm, suicide, or are in immediate danger, please call iCall
        (9152987821), Vandrevala Foundation (1860-2662-345), or 112 immediately.
      </P>

      <H2>4. Account registration &amp; security</H2>
      <UL>
        <li>You agree to provide accurate information and to keep it current.</li>
        <li>You are responsible for all activity that happens under your account. Choose a strong password and keep it confidential.</li>
        <li>One account per person. Sharing accounts is prohibited.</li>
      </UL>

      <H2>5. Booking &amp; payments</H2>
      <UL>
        <li>All prices are displayed in Indian Rupees (₹) and include applicable GST unless otherwise noted.</li>
        <li>Payments are processed by Razorpay. By completing a booking you agree to Razorpay's terms in addition to these Terms.</li>
        <li>Current pricing (subject to change with notice):
          <ul className="list-[circle] pl-5 mt-1">
            <li>Initial Consultation (60 min) — ₹900</li>
            <li>Follow Up (45 min) — ₹600</li>
            <li>Growth Package (3 sessions) — ₹1,500</li>
            <li>Proper Care (weekly sessions for 12 months) — ₹7,200</li>
          </ul>
        </li>
        <li>Bookings are confirmed only after a successful payment and payment verification.</li>
      </UL>

      <H2>6. Conducting the session</H2>
      <UL>
        <li>Sessions are held over a private, end-to-end encrypted Jitsi video room. We do not record sessions.</li>
        <li>You are expected to join from a quiet, private space, on a stable internet connection.</li>
        <li>Please arrive 5 minutes early. Sessions start and end at the scheduled time; late arrivals do not extend the session.</li>
        <li>Do not record, screenshot or share any part of the session with anyone else. Any such conduct will result in immediate termination of your account without refund and may attract legal action.</li>
      </UL>

      <H2>7. Counsellor conduct</H2>
      <P>
        Counsellors on Counseltaria™ are independent, qualified mental-health
        professionals bound by the ethical guidelines of their respective
        associations. Counseltaria™ facilitates the connection and payment but does
        not itself provide clinical advice. Any professional recommendations you
        receive are those of the counsellor, not Counseltaria™.
      </P>

      <H2>8. Refunds &amp; cancellations</H2>
      <P>
        Refunds and cancellations are governed by our <a href="/refund"
           style={{ color: "var(--brand)" }} data-testid="terms-refund-link">Refund &amp; Cancellation
        Policy</a>, which forms an integral part of these Terms.
      </P>

      <H2>9. Intellectual property</H2>
      <P>
        The Counseltaria™ name, logo, website, brand and platform code are the
        intellectual property of Counseltaria™. Content you create as part of your
        account (e.g. profile information, booking notes) remains yours; by uploading
        it, you grant us a limited license to use it solely to operate the service.
      </P>

      <H2>10. Prohibited use</H2>
      <UL>
        <li>Impersonating another person or providing false information.</li>
        <li>Harassment, threats, hate speech or illegal conduct directed at counsellors, staff, or other clients.</li>
        <li>Attempting to record, retransmit or make public any session content.</li>
        <li>Reverse-engineering, scraping or otherwise interfering with the platform.</li>
      </UL>

      <H2>11. Limitation of liability</H2>
      <P>
        To the maximum extent permitted by law, Counseltaria™'s aggregate liability
        for any claim arising out of your use of the service is limited to the fees
        you paid to us for the specific session(s) that gave rise to the claim.
        Counseltaria™ is not liable for indirect, incidental, special or
        consequential damages.
      </P>

      <H2>12. Suspension &amp; termination</H2>
      <P>
        We may suspend or terminate an account without notice for breach of these
        Terms, non-payment, chargebacks, prohibited use, or if required by law. You
        may delete your account at any time from your dashboard.
      </P>

      <H2>13. Changes to these Terms</H2>
      <P>
        We may amend these Terms occasionally. Material changes will be notified via
        email or via a banner in the app at least 7 days before they take effect.
        Continued use after the effective date constitutes acceptance.
      </P>

      <H2>14. Governing law &amp; jurisdiction</H2>
      <P>
        These Terms are governed by the laws of India. Any dispute arising out of or
        in connection with these Terms shall be subject to the exclusive
        jurisdiction of the courts of India.
      </P>

      <H2>15. Contact</H2>
      <P>
        For any questions relating to these Terms, please email <a
          href={`mailto:${BRAND.supportEmail}`} style={{ color: "var(--brand)" }}>
          {BRAND.supportEmail}
        </a>.
      </P>
    </LegalPage>
  );
}
